import React, { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { prependPathPrefix } from 'nextDir/modules/Router/Link';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { format } from 'url';

import authenticate from 'src/common/authenticate';
import { TRACKING_CLASSNAMES } from 'src/common/constants';
import useConfig from 'src/common/hooks/useConfig';
import { useProductGtmPayload } from 'src/common/hooks/useProductGtmQuery';
import { useHostNameAndBaseName } from 'src/common/hooks/useUrl';
import { isApplyFlow } from 'src/common/isApplyFlow';
import { hasInternalRedirect } from 'src/common/isInternalRedirect';
import { getJobLinkPropsFromAsPath, getUtmParams } from 'src/common/links';
import { ROUTES } from 'src/common/routes';
import { trackSocialButtonClicked } from 'src/common/tracking/trackingUtils';
import {
  EmployersLink as SharedEmployersLink,
  TOSLink,
} from 'src/components/links';
import { ExternalSignInButtons } from 'src/components/LoginModal/ExternalSignInButtons';
import { MetadataRenderer } from 'src/components/MetaDescription/MetadataRenderer';
import { OrDivider } from 'src/components/OrDivider';
import { UserRole } from 'src/global/models/User';
import { getOnboardingRedirectURL } from 'src/modules/OnboardingGuided/utils';
import { getMissingMandatorySteps } from 'src/modules/OnboardingGuided/utils/getMissingMandatorySteps';
import { getSourcedJobLinkPropsFromAsPath } from 'src/modules/SourcedJobs/utils';
import { getFetchingMe, getUser } from 'src/selectors/user';
import * as ChooseSignup from 'src/styles/pages/onboarding/chooseSignup';
import { Background, TitleWrapper } from 'src/styles/pages/onboarding/global';

import { Divider, GlintsLink, Title } from './SignUpPage.sc';

const EmployersLink = (...chunks: any[]) => (
  <SharedEmployersLink>{chunks}</SharedEmployersLink>
);

const messages = defineMessages({
  title: {
    id: 'sign-up-page.title-tag',
    defaultMessage: 'Sign Up to Glints Now',
  },
  description: {
    id: 'sign-up-page.description',
    defaultMessage:
      'Sign up to Glints now to get many more jobs from various companies in Indonesia. Easy and fast sign-up with various options.',
  },
});

const Signup: React.FC<React.PropsWithChildren<{ authenticated: boolean }>> = ({
  authenticated,
}) => {
  const me = useSelector(getUser);
  const isFetchingMe = useSelector(getFetchingMe);
  const router = useRouter();
  const { formatMessage } = useIntl();
  const { CANDIDATE_BASE, BASENAME } = useConfig();
  const { next, nextAs, ...queries } = router.query;
  const marketingQueryUtmParams = getUtmParams(queries);
  const asPath = router.asPath;
  const host = useHostNameAndBaseName();

  const isApplyingProcess = isApplyFlow(router);
  const isSignUpFromJobDetailsPage =
    typeof router.query.jobTitle === 'string' && !isApplyingProcess;
  const isSignUpFromSourcedJobsPage =
    typeof router.query.sJobTitle === 'string' && !isApplyingProcess;
  /**
   *  @this isApplyingProcess checks if user is redirected to SignUp after pressing ApplyButton
   *    - If unlogged-in user press ApplyButton, we will redirect back to JDP after signing up + display ApplicationModal
   *  @this isSignUpFromJobDetailsPage checks if user presses SignUp in JDP
   *    - If unlogged-in user presses SignUp button in JDP,  we will redirect back to JDP after signing up
   */

  const signUpURL = {
    pathname: '/signup-email',
    query: router.query,
  };
  const loginURL = {
    pathname: '/login',
    query: router.query,
  };

  const { productGtmPayload } = useProductGtmPayload();

  const getShouldRedirectToNextPath = useCallback(
    () => hasInternalRedirect(router.query),
    [router.query]
  );

  const missingMandatoryStep = getMissingMandatorySteps(me)[0];

  useEffect(() => {
    if (authenticated && !isFetchingMe && me.role) {
      const role = me.role;
      if (role === UserRole.COMPANY) {
        router.push('/redirect');
      } else if (
        isApplyingProcess ||
        isSignUpFromJobDetailsPage ||
        isSignUpFromSourcedJobsPage
      ) {
        if (isSignUpFromJobDetailsPage) {
          const jobLinkProps = getJobLinkPropsFromAsPath(asPath);
          const url = new URL(`${host}${jobLinkProps.as}`);
          window.open(url.toString(), '_self');
          return;
        }
        if (isSignUpFromSourcedJobsPage) {
          const jobLinkProps = getSourcedJobLinkPropsFromAsPath(asPath);
          const url = new URL(`${host}${jobLinkProps.as}`);
          window.open(url.toString(), '_self');
          return;
        }
        router.push(
          { pathname: next?.toString(), query: queries },
          nextAs as any
        );
        return;
      } else if (role === UserRole.CANDIDATE && missingMandatoryStep) {
        router.push({
          pathname: getOnboardingRedirectURL(missingMandatoryStep),
          query: marketingQueryUtmParams,
        });
      } else if (getShouldRedirectToNextPath()) {
        if (typeof nextAs === 'string') {
          router.push({ pathname: next?.toString(), query: queries }, nextAs);
        } else {
          router.push({ pathname: next?.toString(), query: queries });
        }
      } else {
        router.push({
          pathname: `/${ROUTES.opportunitiesJobsRecommended}`,
          query: marketingQueryUtmParams,
        });
      }
    }
  }, [
    authenticated,
    isFetchingMe,
    me,
    router,
    missingMandatoryStep,
    getShouldRedirectToNextPath,
    nextAs,
    next,
    queries,
    isApplyingProcess,
    isSignUpFromJobDetailsPage,
    asPath,
    host,
    marketingQueryUtmParams,
    isSignUpFromSourcedJobsPage,
  ]);

  return (
    <Background>
      <MetadataRenderer
        title={formatMessage(messages.title)}
        description={formatMessage(messages.description)}
        canonical={`${CANDIDATE_BASE}${
          BASENAME !== '/' ? BASENAME : ''
        }/signup`}
        glintsTrackingName="signup"
      />
      <TitleWrapper>
        <Title>
          <FormattedMessage
            id="sign-up-page.title"
            defaultMessage="Let's create{linebreak}your Glints profile"
            tagName="p"
            values={{
              linebreak: <br />,
            }}
          />
        </Title>
      </TitleWrapper>
      <ChooseSignup.Wrapper>
        <ChooseSignup.Content>
          <ChooseSignup.UpperInnerContent>
            <ExternalSignInButtons
              signInSource="sign up"
              gtmPayloadString={productGtmPayload}
            />
            <OrDivider />
            <GlintsLink
              href={prependPathPrefix(BASENAME, format(signUpURL))}
              aria-label="Sign up with Email button"
              onClick={() => {
                trackSocialButtonClicked({
                  type: 'email',
                  page: router.asPath.includes('signup') ? 'signup' : 'login',
                });
              }}
            >
              <FormattedMessage
                id="signup.button.email.signup"
                defaultMessage="Sign up with Email"
              />
            </GlintsLink>

            <br />
            <ChooseSignup.ToCBox>
              <FormattedMessage
                id="signup.terms"
                defaultMessage="By creating an account here, I understand and agree to the { link }"
                values={{
                  link: <TOSLink style={{ textDecoration: 'underline' }} />,
                }}
                tagName="span"
              />
            </ChooseSignup.ToCBox>
          </ChooseSignup.UpperInnerContent>
          <Divider />
          <ChooseSignup.LowerInnerContent>
            <ChooseSignup.SignUpSmallText>
              <ChooseSignup.Paragraph>
                <span>
                  <FormattedMessage
                    id="already.have.a.glints.account"
                    defaultMessage="Already have a Glints account?"
                  />
                </span>
              </ChooseSignup.Paragraph>
              <GlintsLink
                href={prependPathPrefix(BASENAME, format(loginURL))}
                className={TRACKING_CLASSNAMES.loginSubmitButton}
              >
                <FormattedMessage id="login" defaultMessage="Login" />
              </GlintsLink>
            </ChooseSignup.SignUpSmallText>
            <ChooseSignup.SignUpSmallText>
              <FormattedMessage
                id="login-page_employers-link"
                defaultMessage="If you’re an employer, visit <EmployersLink>this page</EmployersLink> instead."
                tagName="p"
                values={{
                  EmployersLink,
                }}
              />
            </ChooseSignup.SignUpSmallText>
          </ChooseSignup.LowerInnerContent>
        </ChooseSignup.Content>
      </ChooseSignup.Wrapper>
    </Background>
  );
};

export default authenticate(Signup, true);
